import { useEffect, useState } from "react";
import { Button, Descriptions, Form, InputNumber, Space, Statistic, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PutQuotaRequest, PutQuotaResponse, QuotaState } from "@coco-mailrelay/shared-types";
import { apiRequest } from "../../lib/apiRequest";
import { errorMessage } from "../../lib/errors";
import { GaCancelledError } from "../../lib/ga-interceptor";

/** View + configure tenant quota (docs/305 §3.6). PUT is a step-up op. */
export default function QuotaTab({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PutQuotaRequest>();

  const quotaQuery = useQuery({
    queryKey: ["quota", tenantId],
    queryFn: () => apiRequest<QuotaState>(`/tenants/${tenantId}/quota`),
  });

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (quotaQuery.data) form.setFieldsValue({ quota_per_min: quotaQuery.data.quota_per_min });
  }, [quotaQuery.data, form]);

  const putMutation = useMutation({
    mutationFn: (body: PutQuotaRequest) =>
      apiRequest<PutQuotaResponse>(`/tenants/${tenantId}/quota`, { method: "PUT", body }),
    onSuccess: () => {
      message.success("Quota updated");
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["quota", tenantId] });
      void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to update quota"));
    },
  });

  const data = quotaQuery.data;
  const bucket = data?.bucket ?? null;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Descriptions title="Current quota" column={1} bordered size="small">
        <Descriptions.Item label="quota_per_min">{data?.quota_per_min ?? "—"}</Descriptions.Item>
      </Descriptions>

      {bucket && (
        <Space size="large">
          <Statistic title="Tokens" value={bucket.tokens} precision={2} />
          <Statistic title="Capacity" value={bucket.capacity} />
          <Statistic title="Refill / sec" value={bucket.refill_per_sec} precision={4} />
        </Space>
      )}

      {!editing ? (
        <Button type="primary" onClick={() => setEditing(true)}>
          Configure quota
        </Button>
      ) : (
        <Form<PutQuotaRequest> form={form} layout="inline" onFinish={(v) => putMutation.mutate(v)}>
          <Form.Item
            name="quota_per_min"
            label="Quota per minute"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber min={1} max={1_000_000} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={putMutation.isPending}>
                Save
              </Button>
              <Button onClick={() => setEditing(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Space>
  );
}
